import { Suspense, useEffect } from 'react';
import type { MenuProps } from 'antd';
import {
  Avatar,
  Breadcrumb,
  ConfigProvider,
  Dropdown,
  Layout,
  Menu,
  Skeleton,
  theme,
} from 'antd';
import { useStore } from '@/store';
import ErrorBoundary from '@/components/ErrorBoundary';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { getStorage } from '@/utils/storage';
import { C_PROJECT_NAME, C_STORAGE } from '@/constant';
import { filter, find, head, isEmpty, last, map, size } from 'lodash';
import NProgress from '@/components/NProgress';
import { IconMenuFold, IconMenuUnfold } from '@emooa/icon';
import { observer, useLocalObservable } from 'mobx-react-lite';
import LinkTags from './LinkTags';
import logo from '@/assets/images/logo.png';
import { authApi } from '@/apis';
import { getRandomColor } from '@/utils/color';
import { findPathByKey } from '@/utils';
import { Button, Space } from '@emooa/ui';
import React from 'react';
import * as AntdIcons from '@ant-design/icons';
import { businessMenus } from '@/routes';

const allIcons: Record<string, any> = AntdIcons;

const { Header, Content, Sider } = Layout;

const Layouts = observer(() => {
  const store = useStore();
  const state = useLocalObservable(
    (): {
      collapsed: boolean;
      setCollapsed: Function;
    } => ({
      collapsed: false,
      setCollapsed(collapsed: boolean) {
        this.collapsed = collapsed;
      },
    }),
  );
  const location = useLocation();
  const navigate = useNavigate();

  // const websocket = async () => {
  // try {
  //   const total: any = await systemApi.getUnReadMessage();
  //   total == 'undefined' ? 0 : total;
  //   const { list = [] }: any = await systemApi.getSysMessageList({
  //     pageNum: 1,
  //     pageSize: 6,
  //   });
  //   store.setMessages({
  //     list,
  //     total: total || 0,
  //   });
  //   store.socket = new Socket('', {
  //     onmessage: async () => {
  //       try {
  //         const total: any = await systemApi.getUnReadMessage();
  //         const { list = [] }: any = await systemApi.getSysMessageList({
  //           pageNum: 1,
  //           pageSize: 6,
  //         });
  //         store.setMessages({
  //           list,
  //           total: total || 0,
  //         });
  //       } catch (error) {
  //         console.error('WebSocket message handler error:', error);
  //         // 确保即使API调用失败，也能设置一个默认值
  //         store.setMessages({
  //           list: [],
  //           total: 0,
  //         });
  //       }
  //     },
  //   });
  // } catch (error) {
  //   console.error('WebSocket initialization error:', error);
  //   // 初始化失败时设置默认值
  //   store.setMessages({
  //     list: [],
  //     total: 0,
  //   });
  // }
  // };

  useEffect(() => {
    const auth = getStorage(C_STORAGE.AUTH);
    if (!isEmpty(auth)) {
      getInfo();
    } else {
      console.error('未找到用户信息，正在跳转到登录页');
      navigate('/login');
    }
  }, [store.auth]);

  useEffect(() => {
    if (!isEmpty(store.auth)) {
      const menus = wrapMenu(businessMenus);
      store.setMenu(menus);
      const pathKeys = findPathByKey(menus);
      if (size(pathKeys) >= 2) {
        store.setTags([head(pathKeys), last(pathKeys)]);
      } else {
        if (head(pathKeys)) {
          store.setTags([head(pathKeys)]);
        } else {
          store.setTags([]);
        }
      }
    }
  }, [store.auth]);

  const getInfo = async () => {
    const info: API.Auth.AuthInfo = await authApi.getInfo();
    if (info) {
      store.setAuth(info);
    } else {
      console.error('获取用户信息失败');
    }
  };

  const wrapMenu = (
    menu: API.RoutersMenuItem[],
    parentPath: string = '',
  ): API.MenuItem[] => {
    return filter(menu, item => {
      if (item.hidden) return false;
      if (isEmpty(item.children)) {
        return true;
      }
      const children = wrapMenu(
        item.children || [],
        joinMenuPath(parentPath, item.path),
      );
      return !isEmpty(children);
    }).map(item => {
      const path = joinMenuPath(parentPath, item.path);
      if (isEmpty(item.children)) {
        return {
          key: path,
          label: (
            <Link
              to={`${path}${item.query || ''}`}
              onClick={() =>
                onClick({
                  key: path,
                  label: item.meta?.title,
                  query: item.query,
                })
              }
              target={item.meta?.link ? '_blank' : '_self'}
            >
              {item.meta?.title}
            </Link>
          ),
          icon: createIcon(item.meta.icon),
          meta: item.meta,
          query: item.query,
        };
      } else {
        return {
          key: path,
          label: item.meta?.title,
          icon: createIcon(item.meta.icon),
          children: wrapMenu(
            item.children || [],
            joinMenuPath(parentPath, item.path),
          ),
          meta: item.meta,
          query: item.query,
        };
      }
    });
  };

  const joinMenuPath = (parentPath: string, path: string) => {
    const nextPath = parentPath
      ? `${parentPath.replace(/\/$/, '')}/${path.replace(/^\//, '')}`
      : `/${path.replace(/^\//, '')}`;
    return nextPath === '/' ? nextPath : nextPath.replace(/\/$/, '');
  };

  const openKey = `/${location.pathname.split('/').filter(Boolean)[0] || ''}`;

  function createIcon(icon: string | any): React.ReactNode | string {
    if (typeof icon === 'object') {
      return icon;
    }
    const ele = allIcons[icon];
    if (ele) {
      return React.createElement(allIcons[icon]);
    }
    return '';
  }

  const onClick = (item: API.MenuItem) => {
    if (
      find(store.tags, tag => {
        const regex = new RegExp(
          `^${tag.key.replace(/:\w+/g, '\\w+').replace('*', '.*')}$`,
        );
        return item.key && regex.test(item.key);
      })
    ) {
      return;
    }
    store.setTags([...store.tags, item]);
  };

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const remove = (item: API.MenuItem) => {
    store.setTags(
      filter(store.tags, (tag: API.MenuItem) => tag.key !== item.key),
    );

    if (item.key === location.pathname) {
      const found = last(store.tags);
      if (!isEmpty(found)) {
        const { key, query = '' } = found;
        navigate(`${key}${query}`);
      }
    }
  };

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <Link
          key="/auth"
          to="/auth"
          onClick={() =>
            onClick({
              key: '/auth',
              label: '个人中心',
            })
          }
        >
          个人中心
        </Link>
      ),
    },
    {
      key: '2',
      label: (
        <span
          onClick={() => {
            store.setAuth({});
            navigate('/login');
          }}
        >
          退出登录
        </span>
      ),
    },
  ];

  return (
    <Layout
      style={{
        background: colorBgContainer,
      }}
    >
      <Sider
        width={250}
        collapsed={state.collapsed}
        theme="light"
        className="pb-7 mb-5"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          zIndex: 1,
        }}
        title={C_PROJECT_NAME}
      >
        <div className="p-5 flex items-center">
          <img src={logo} className="h-10 mr-2.5" />
          <span
            style={{
              transition: '1s all',
              display: !state.collapsed ? 'block' : 'none',
            }}
            className="whitespace-nowrap font-bold"
          >
            {C_PROJECT_NAME}
          </span>
        </div>
        <ConfigProvider
        // theme={{
        //   components: {
        //     Menu: {
        //       colorText: '#bfcbd9',
        //       colorBgContainer: '#304056',
        //       subMenuItemBg: '#1f2d3d',
        //       itemHoverBg: '#1a2735',
        //       itemSelectedBg: '#1a2735',
        //       colorBgElevated: '#304056',
        //       colorPrimary: '#3dabe2',
        //     },
        //   },
        // }}
        >
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={[location.pathname]}
            defaultOpenKeys={openKey === '/' ? [] : [openKey]}
            items={store.menu}
          />
        </ConfigProvider>
      </Sider>
      <Layout
        className={`ml-${state.collapsed ? 80 : 250}`}
        style={{
          transition: '0.3s all',
          minHeight: '100vh',
          marginLeft: state.collapsed ? '80px' : '250px',
        }}
      >
        <Header
          className="p-0 h-12"
          style={{
            background: colorBgContainer,
            boxShadow: '0 1px 4px rgba(0,21,41,.08)',
          }}
        >
          <div className="pr-5 h-12 flex justify-between">
            <div className="flex items-center">
              <Button
                type="text"
                icon={state.collapsed ? <IconMenuUnfold /> : <IconMenuFold />}
                onClick={() => state.setCollapsed(!state.collapsed)}
                style={{
                  fontSize: '16px',
                  height: 48,
                  width: 48,
                }}
              />
              <Breadcrumb
                className="mr-4"
                items={map(findPathByKey(store.menu), item => ({
                  path: item.key ? `${item.key}${item.query || ''}` : undefined,
                  title: item.label,
                  query: item.query,
                }))}
                itemRender={item => {
                  return !item.path ? (
                    <span key={item.path}>{item.title}</span>
                  ) : (
                    <div onClick={() => navigate(item.path || '/')}>
                      {item.title}
                    </div>
                  );
                }}
              />
            </div>
            <Space size={[20, 10]}>
              <Dropdown menu={{ items }} placement="bottomLeft" arrow>
                <Avatar
                  shape="square"
                  className="cursor-pointer"
                  style={{
                    backgroundColor: getRandomColor(),
                  }}
                >
                  {store.auth?.user?.nickName}
                </Avatar>
              </Dropdown>
            </Space>
          </div>
        </Header>
        <LinkTags tags={store.tags} remove={remove} />
        <Content
          className="p-6"
          style={{
            background: colorBgContainer,
          }}
        >
          <ErrorBoundary>
            <Suspense
              fallback={
                <>
                  <NProgress />
                  <Skeleton active className="mt-5" />
                </>
              }
            >
              {!isEmpty(store.auth) ? <Outlet /> : ''}
            </Suspense>
          </ErrorBoundary>
        </Content>
      </Layout>
    </Layout>
  );
});

export default Layouts;
